import {
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  LoadingOverlay,
  Modal,
  NumberInput,
  Radio,
  Select,
  Stack,
  Stepper,
  Switch,
  Table,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { PiCheck, PiPaperPlaneTilt, PiWarning } from 'react-icons/pi';
import {
  type TAmendedInvoice,
  type TAmendIssuerOption,
  type TAmendRequest,
  type TAmendResult,
  useAmendInvoice,
  useAmendOptions,
  useSendInvoice,
} from '@/models/invoices';
import type { TInvoice } from '@/types';

const money = (amount: number, currency: string) =>
  `${currency} ${amount.toFixed(2)}`;

const pct = (rate: number) =>
  `${(rate * 100).toFixed(2).replace(/\.00$/, '')}%`;

interface Props {
  invoice: TInvoice;
  opened: boolean;
  onClose: () => void;
}

export function AmendInvoiceModal({ invoice, opened, onClose }: Props) {
  const invoiceId = Number(invoice.id);

  const [step, setStep] = useState(0);
  const [payerId, setPayerId] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string | null>(null);
  const [issuerId, setIssuerId] = useState<number | null>(null);
  const [vatOn, setVatOn] = useState(false);
  const [clearVat, setClearVat] = useState(false);
  const [revertVat, setRevertVat] = useState(false);
  const [vatRate, setVatRate] = useState<number | null>(null);
  const [vatReason, setVatReason] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<TAmendResult | null>(null);
  const [committed, setCommitted] = useState<TAmendResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Eligibility follows what the accountant is currently considering, not what
  // the invoice happens to hold - changing the payer moves which companies may
  // legally issue, and the list must say so before they submit.
  const options = useAmendOptions(
    invoiceId,
    {
      payerId: payerId ?? undefined,
      paymentMethod: paymentMethod ?? undefined,
      currency: currency ?? undefined,
      invoicingCompanyId: issuerId ?? undefined,
    },
    opened,
  );
  const amend = useAmendInvoice(invoice.offerId ?? undefined);
  const send = useSendInvoice();

  const data = options.data;
  const effectivePayerId = payerId ?? data?.current.payerId ?? null;
  const effectiveMethod = paymentMethod ?? data?.current.paymentMethod ?? '';
  const effectiveIssuerId =
    issuerId ?? data?.current.invoicingCompanyId ?? null;
  const isCrypto = effectiveMethod === 'Cryptocurrency';

  const selectedIssuer = data?.issuers.find((i) => i.id === effectiveIssuerId);
  // A company with no rail cannot be paid, so the invoice cannot say where to
  // pay - unlike a self-dealing bar, no permission and no reason lifts it. It
  // reaches this screen only when the currency or method just moved away from
  // what the invoice's own issuer holds.
  const issuerUnpayable = selectedIssuer?.blockRule === 'RAIL';
  // An issuer that levies no VAT has no rate to state or override. Its
  // invoices print no rate at all, so the whole VAT section is meaningless -
  // except where a manual rate stands, which has to be cleared deliberately.
  const issuerLeviesVat = selectedIssuer?.vatApplicable !== false;
  const mustClearVat = !issuerLeviesVat && data?.current.vatOverridden === true;
  const issuerBarred = selectedIssuer
    ? !selectedIssuer.eligible && !issuerUnpayable
    : false;

  // Bank transfer on an invoice that went through crypto has no remembered
  // currency to fall back on, so it has to be chosen explicitly.
  const mustChooseCurrency =
    !isCrypto && data?.requiresExplicitCurrency && !currency;

  const request = useMemo<TAmendRequest>(() => {
    const body: TAmendRequest = { id: invoiceId };
    if (payerId != null) body.payerId = payerId;
    if (paymentMethod) body.paymentMethod = paymentMethod;
    if (currency && !isCrypto) body.currency = currency;
    if (issuerId != null) body.invoicingCompanyId = issuerId;
    if (issuerBarred) body.overrideSelfDealing = true;
    // A rate left over from an earlier issuer choice must not be sent to one
    // that levies none: the amendment would be refused, and the accountant
    // would be told about a control the form no longer shows.
    if (vatOn && issuerLeviesVat) {
      body.vat = {
        rate: vatRate ?? 0,
        reason: vatReason,
      };
    }
    if (revertVat && !vatOn) body.revertVatToAutomatic = true;
    if (clearVat) body.clearVatOverride = true;
    if (reason) body.reason = reason;
    return body;
  }, [
    invoiceId,
    payerId,
    paymentMethod,
    currency,
    isCrypto,
    issuerId,
    issuerBarred,
    vatOn,
    vatRate,
    vatReason,
    revertVat,
    clearVat,
    issuerLeviesVat,
    reason,
  ]);

  const changedSomething = Object.keys(request).length > 1;

  const blockingIssue = (() => {
    if (!data) return 'Loading';
    if (!data.amendable) {
      return `Only an issued invoice can be amended; this one is ${data.status}`;
    }
    if (!changedSomething) return 'Change something first';
    if (mustChooseCurrency) return 'Choose the bank transfer currency';
    if (issuerUnpayable) {
      return (
        selectedIssuer?.blockText ??
        'This issuing company cannot be paid this way'
      );
    }
    if (issuerBarred && !data.mayOverrideSelfDealing) {
      return 'You may not issue from a company barred by the self-dealing rules';
    }
    if (issuerBarred && !reason.trim()) {
      return 'A reason is required to override self-dealing prevention';
    }
    if (mustClearVat && !clearVat) {
      return 'Clear the manual VAT rate to use this issuing company';
    }
    // The VAT editor is not shown for an issuer that levies none, so a rate
    // left in state from an earlier choice is not sent and not validated.
    if (issuerLeviesVat && vatOn && vatRate == null)
      return 'Enter the VAT rate';
    if (issuerLeviesVat && vatOn && !vatReason.trim()) {
      return 'A VAT override needs a reason';
    }
    return null;
  })();

  const runPreview = async () => {
    setError(null);
    try {
      const result = await amend.mutateAsync({ ...request, dryRun: true });
      setPreview(result);
      setStep(1);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const commit = async () => {
    setError(null);
    try {
      const result = await amend.mutateAsync(request);
      setCommitted(result);
      setStep(2);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const close = () => {
    setStep(0);
    setPreview(null);
    setCommitted(null);
    setError(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      size="xl"
      title={`Amend invoice ${invoice.invoiceNumber ?? ''}`}
    >
      <Stepper active={step} size="sm" mb="md">
        <Stepper.Step label="Change" />
        <Stepper.Step label="Review impact" />
        <Stepper.Step label="Done" />
      </Stepper>

      {options.isLoading && (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      )}

      {error && (
        <Alert color="red" icon={<PiWarning />} mb="md">
          {error}
        </Alert>
      )}

      <Box pos="relative">
        <LoadingOverlay
          visible={options.isFetching && !options.isLoading}
          zIndex={100}
          overlayProps={{ blur: 0, backgroundOpacity: 0.35 }}
          loaderProps={{ size: 'sm' }}
        />

        {step === 0 && data && (
          <Stack gap="md">
            {data.current.partNumber != null && (
              <Alert color="blue">
                This is installment {data.current.partNumber}. The change
                applies to this installment and every later unpaid one;
                installments the client has already confirmed or paid are left
                untouched and listed on the next step.
              </Alert>
            )}

            <Select
              label="Bill to"
              description="Drives the VAT treatment and which companies may issue"
              value={effectivePayerId ? String(effectivePayerId) : null}
              onChange={(v) => setPayerId(v ? Number(v) : null)}
              data={data.payers.map((p) => ({
                value: String(p.id),
                label: p.eligible
                  ? `${p.name} · ${p.countryCode} · ${p.clientType}`
                  : `${p.name} · ${p.blockText}`,
                disabled: !p.eligible,
              }))}
            />

            <Group grow align="flex-start">
              <Select
                label="Payment method"
                value={effectiveMethod}
                onChange={(v) => {
                  setPaymentMethod(v);
                  if (v === 'Cryptocurrency') setCurrency(null);
                }}
                data={['Bank Transfer', 'Cryptocurrency']}
              />
              <Select
                label="Currency"
                description={
                  isCrypto
                    ? 'Cryptocurrency invoices are always USD'
                    : mustChooseCurrency
                      ? 'This invoice was issued in crypto, so it does not remember a bank currency'
                      : undefined
                }
                disabled={isCrypto}
                error={mustChooseCurrency ? 'Required' : undefined}
                value={
                  isCrypto
                    ? 'USD'
                    : (currency ?? data.current.bankCurrency ?? null)
                }
                onChange={setCurrency}
                data={['EUR', 'USD', 'GBP']}
              />
            </Group>

            <IssuerPicker
              issuers={data.issuers}
              value={effectiveIssuerId}
              onChange={setIssuerId}
              mayOverride={data.mayOverrideSelfDealing}
            />

            {issuerUnpayable && (
              <Alert color="red" icon={<PiWarning />}>
                <Text size="sm" fw={600}>
                  {selectedIssuer?.name} cannot be paid this way.
                </Text>
                <Text size="sm">{selectedIssuer?.blockText}.</Text>
              </Alert>
            )}

            {issuerBarred && (
              <Alert color="red" icon={<PiWarning />}>
                <Text size="sm" fw={600}>
                  This overrides self-dealing prevention (
                  {selectedIssuer?.blockRule}).
                </Text>
                <Text size="sm">{selectedIssuer?.blockText}.</Text>
              </Alert>
            )}

            <Divider label="VAT" />

            {!issuerLeviesVat ? (
              <Alert color="grape" variant="light">
                <Text size="sm">
                  {selectedIssuer?.name} levies no VAT. Its invoices state no
                  rate at all, so there is nothing to set here.
                </Text>
                {mustClearVat && (
                  <>
                    <Text size="sm" mt="xs">
                      This invoice carries a manually set VAT rate, which this
                      issuer cannot carry. Confirm clearing it to continue.
                    </Text>
                    <Switch
                      mt="xs"
                      size="sm"
                      label="Clear the manual VAT rate"
                      checked={clearVat}
                      onChange={(e) => setClearVat(e.currentTarget.checked)}
                    />
                  </>
                )}
              </Alert>
            ) : (
              <>
                <VatComparison
                  automaticRate={data.automaticVat?.vatRate ?? null}
                  automaticNote={data.automaticVat?.vatNote ?? null}
                  derivation={
                    data.automaticVat
                      ? `${data.automaticVat.buyerType.toLowerCase()} buyer in ${data.automaticVat.buyerCountry}, billed from ${data.automaticVat.companyCountry}`
                      : null
                  }
                  manualRate={
                    vatOn
                      ? vatRate
                      : revertVat
                        ? null
                        : data.current.vatOverridden
                          ? data.current.vatRate
                          : null
                  }
                />

                {data.current.vatOverridden && !vatOn && (
                  <Alert color="yellow">
                    <Text size="sm">
                      VAT is set manually. It stays as it is when the payer or
                      issuing company changes, until somebody amends it again.
                    </Text>
                    {data.current.vatOverrideReason && (
                      <Text size="xs" c="dimmed" mt={4}>
                        Reason: {data.current.vatOverrideReason}
                      </Text>
                    )}
                    <Switch
                      mt="xs"
                      size="sm"
                      label="Revert to the automatic VAT"
                      checked={revertVat}
                      onChange={(e) => setRevertVat(e.currentTarget.checked)}
                    />
                  </Alert>
                )}

                <Switch
                  label={
                    data.current.vatOverridden
                      ? 'Set a different VAT rate'
                      : 'Set VAT manually'
                  }
                  checked={vatOn}
                  onChange={(e) => {
                    const on = e.currentTarget.checked;
                    setVatOn(on);
                    // Start from the rate actually in force, so opening the editor
                    // cannot make a standing override look like it vanished.
                    if (on && vatRate == null && data.current.vatOverridden) {
                      setVatRate(data.current.vatRate);
                    }
                  }}
                />

                {vatOn && (
                  <Stack gap="xs" pl="md">
                    <NumberInput
                      label="VAT rate"
                      suffix=" %"
                      min={0}
                      max={100}
                      decimalScale={2}
                      required
                      value={vatRate == null ? '' : vatRate * 100}
                      onChange={(v) =>
                        setVatRate(v === '' ? null : Number(v) / 100)
                      }
                    />

                    <Textarea
                      label="Reason"
                      description="Recorded in the audit log, never printed"
                      required
                      autosize
                      minRows={2}
                      value={vatReason}
                      onChange={(e) => setVatReason(e.currentTarget.value)}
                    />
                  </Stack>
                )}
              </>
            )}

            <Divider />

            <Textarea
              label="Reason for this amendment"
              description={
                issuerBarred
                  ? 'Required when overriding self-dealing prevention'
                  : 'Optional, recorded in the audit log'
              }
              required={issuerBarred}
              autosize
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />

            <Group justify="flex-end">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Tooltip label={blockingIssue} disabled={!blockingIssue}>
                <Button
                  onClick={runPreview}
                  loading={amend.isPending}
                  disabled={!!blockingIssue}
                >
                  Review impact
                </Button>
              </Tooltip>
            </Group>
          </Stack>
        )}

        {step === 1 && preview && (
          <Stack gap="md">
            <ImpactTable result={preview} invoice={invoice} />

            {preview.skipped.length > 0 && (
              <Alert color="yellow" icon={<PiWarning />}>
                {preview.skipped.length === 1
                  ? 'One later installment cannot be amended and is left unchanged. Handle it separately.'
                  : `${preview.skipped.length} later installments cannot be amended and are left unchanged. Handle them separately.`}
              </Alert>
            )}

            <Alert color="gray">
              Nothing is sent to the client. After amending you can download the
              PDF, check it, and send it yourself.
            </Alert>

            <Group justify="space-between">
              <Button variant="default" onClick={() => setStep(0)}>
                Back
              </Button>
              <Button onClick={commit} loading={amend.isPending} color="orange">
                Amend{' '}
                {preview.amended.length > 1
                  ? `${preview.amended.length} invoices`
                  : 'invoice'}
              </Button>
            </Group>
          </Stack>
        )}

        {step === 2 && committed && (
          <Stack gap="md">
            <Alert color="green" icon={<PiCheck />}>
              {committed.amended.length > 1
                ? `${committed.amended.length} invoices are now at a new version and have not been sent.`
                : 'The invoice is now at a new version and has not been sent.'}
            </Alert>

            <Table withTableBorder>
              <Table.Tbody>
                {committed.amended.map((a) => (
                  <Table.Tr key={a.id}>
                    <Table.Td>{a.invoiceNumber}</Table.Td>
                    <Table.Td>
                      <Badge variant="light">v{a.documentVersion}</Badge>
                    </Table.Td>
                    <Table.Td>{money(a.payableAmount, a.currency)}</Table.Td>
                    <Table.Td>
                      <Badge color="orange" variant="light">
                        not sent
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="compact-xs"
                        variant="default"
                        leftSection={<PiPaperPlaneTilt size={12} />}
                        loading={send.isPending}
                        onClick={() => send.mutate({ id: a.id })}
                      >
                        Send
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            <Group justify="flex-end">
              <Button variant="default" onClick={close}>
                Close
              </Button>
              {committed.amended.length > 1 && (
                <Button
                  leftSection={<PiPaperPlaneTilt size={14} />}
                  loading={send.isPending}
                  onClick={async () => {
                    for (const a of committed.amended) {
                      await send.mutateAsync({ id: a.id });
                    }
                    close();
                  }}
                >
                  Send all to client
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Box>
    </Modal>
  );
}

/**
 * Both answers, side by side: what the platform derives and what the accountant
 * has stated. Showing only the winner hides the fact that a manual rate is a
 * departure, which is precisely the thing a reviewer needs to see.
 */
function VatComparison({
  automaticRate,
  automaticNote,
  derivation,
  manualRate,
}: {
  automaticRate: number | null;
  automaticNote: string | null;
  derivation: string | null;
  manualRate: number | null;
}) {
  const overridden = manualRate != null;
  return (
    <Table withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>VAT</Table.Th>
          <Table.Th>Rate</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        <Table.Tr>
          <Table.Td>
            <Text size="sm" c={overridden ? 'dimmed' : undefined}>
              Matrix
            </Text>
            {derivation && (
              <Text size="xs" c="dimmed">
                {derivation}
              </Text>
            )}
            {automaticNote && (
              <Text size="xs" c="dimmed" fs="italic">
                {automaticNote}
              </Text>
            )}
          </Table.Td>
          <Table.Td>
            <Text
              size="sm"
              fw={overridden ? 400 : 700}
              c={overridden ? 'dimmed' : undefined}
              td={overridden ? 'line-through' : undefined}
            >
              {automaticRate == null ? '—' : pct(automaticRate)}
            </Text>
          </Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Td>
            <Group gap={6}>
              <Text size="sm">Manual</Text>
              {overridden && (
                <Badge size="xs" color="yellow" variant="light">
                  applies
                </Badge>
              )}
            </Group>
          </Table.Td>
          <Table.Td>
            <Text
              size="sm"
              fw={overridden ? 700 : 400}
              c={overridden ? undefined : 'dimmed'}
            >
              {overridden ? pct(manualRate) : 'not set'}
            </Text>
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );
}

function IssuerPicker({
  issuers,
  value,
  onChange,
  mayOverride,
}: {
  issuers: TAmendIssuerOption[];
  value: number | null;
  onChange: (id: number) => void;
  mayOverride: boolean;
}) {
  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        Issuing company
      </Text>
      <Radio.Group
        value={value ? String(value) : null}
        onChange={(v) => onChange(Number(v))}
      >
        <Stack gap={4}>
          {issuers.map((c) => {
            // Barred companies stay visible with their reason: hiding them
            // turns "why can't I pick Panama" into a support question.
            // Self-dealing bars yield to the override permission; a missing
            // payment rail does not, because no permission conjures a bank
            // account into existence.
            const selectable =
              c.eligible || (mayOverride && c.blockRule !== 'RAIL');
            return (
              <Radio
                key={c.id}
                value={String(c.id)}
                disabled={!selectable}
                label={
                  <Group gap="xs">
                    <Text size="sm">
                      {c.name} ({c.countryCode})
                    </Text>
                    {c.vatRate != null && (
                      <Badge size="xs" variant="light" color="blue">
                        VAT {pct(c.vatRate)}
                      </Badge>
                    )}
                    {c.eligible ? (
                      <Text size="xs" c="dimmed">
                        priority {c.priority}
                      </Text>
                    ) : (
                      <Badge size="xs" color="red" variant="light">
                        {c.blockRule === 'RAIL'
                          ? c.blockText
                          : `${c.blockRule}: ${c.blockText}`}
                      </Badge>
                    )}
                  </Group>
                }
              />
            );
          })}
        </Stack>
      </Radio.Group>
    </Stack>
  );
}

function ImpactTable({
  result,
  invoice,
}: {
  result: TAmendResult;
  invoice: TInvoice;
}) {
  const before = {
    subtotal: Number(invoice.subtotalAmount ?? 0),
    vatAmount: Number(invoice.vatAmount ?? 0),
    total: Number(invoice.totalAmount ?? 0),
    // Compare like with like: what the client owes now against what they will
    // owe, both in the document currency. `dueAmount` on the row is the
    // invoice as issued, before any discount, and is not that figure.
    due: Number(invoice.money?.payable ?? invoice.totalAmount ?? 0),
    currency: String(invoice.baseCurrency ?? invoice.dueCurrency ?? 'EUR'),
  };
  const target = result.amended.find(
    (a) => Number(a.id) === Number(invoice.id),
  );

  return (
    <Stack gap="sm">
      {target && (
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              <Table.Th>Before</Table.Th>
              <Table.Th>After</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <MoneyRow
              label="Subtotal"
              before={money(before.subtotal, before.currency)}
              after={money(target.subtotalAmount, target.currency)}
            />
            <MoneyRow
              label={`VAT (${pct(target.vatRate)})`}
              before={money(before.vatAmount, before.currency)}
              after={money(target.vatAmount, target.currency)}
            />
            <MoneyRow
              label="Total"
              before={money(before.total, before.currency)}
              after={money(target.totalAmount, target.currency)}
            />
            <MoneyRow
              label="Due"
              before={money(before.due, before.currency)}
              after={money(target.payableAmount, target.currency)}
            />
          </Table.Tbody>
        </Table>
      )}

      {(result.amended.length > 1 ||
        result.skipped.length > 0 ||
        result.inheritedPlanItemIds.length > 0) && (
        <>
          <Text size="sm" fw={500}>
            Installments
          </Text>
          <Table withTableBorder>
            <Table.Tbody>
              {result.amended.map((a: TAmendedInvoice) => (
                <Table.Tr key={a.id}>
                  <Table.Td>{a.invoiceNumber}</Table.Td>
                  <Table.Td>{money(a.payableAmount, a.currency)}</Table.Td>
                  <Table.Td>
                    <Badge color="orange" variant="light">
                      amended
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
              {result.skipped.map((s) => (
                <Table.Tr key={s.invoiceId}>
                  <Table.Td>Installment {s.seq}</Table.Td>
                  <Table.Td>{s.status}</Table.Td>
                  <Table.Td>
                    <Badge color="gray" variant="light">
                      skipped — handle separately
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
              {result.inheritedPlanItemIds.length > 0 && (
                <Table.Tr>
                  <Table.Td colSpan={2}>
                    {result.inheritedPlanItemIds.length} installment(s) not yet
                    issued
                  </Table.Td>
                  <Table.Td>
                    <Badge color="blue" variant="light">
                      inherit the new values
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </>
      )}
    </Stack>
  );
}

function MoneyRow({
  label,
  before,
  after,
}: {
  label: string;
  before: string;
  after: string;
}) {
  const moved = before !== after;
  return (
    <Table.Tr>
      <Table.Td>{label}</Table.Td>
      <Table.Td>
        <Text size="sm" td={moved ? 'line-through' : undefined} c="dimmed">
          {before}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={moved ? 700 : 400}>
          {after}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}

function errorText(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) {
    return String((e as { message: unknown }).message);
  }
  return 'The amendment could not be applied';
}

export default AmendInvoiceModal;
